import { useState } from "react";
import { content } from "../i18n";
import { MaskLine, Reveal } from "../components/Motion";
import { SubscribeForm } from "../components/SubscribeForm";

const LangToggle = ({ lang, setLang }) => (
  <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-ink-faint">
    {["es", "en"].map((l, i) => (
      <span key={l} className="flex items-center gap-3">
        {i === 1 && <span className="text-ink/20">·</span>}
        <button
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`transition-colors duration-500 ${
            lang === l ? "text-ink" : "hover:text-ink-soft"
          }`}
        >
          {l}
        </button>
      </span>
    ))}
  </div>
);

const Label = ({ children }) => (
  <span className="block text-xs tracking-[0.28em] uppercase text-ink-faint mb-8">
    {children}
  </span>
);

const shell = "mx-auto w-full max-w-[680px] px-6";

export default function Landing() {
  const [lang, setLang] = useState("es");
  const t = content[lang];

  return (
    <div className="min-h-screen bg-sand text-ink">
      {/* HEADER */}
      <header className={`${shell} pt-10 flex items-center justify-between`}>
        <span className="font-serif text-lg text-ink">Paco Montero</span>
        <LangToggle lang={lang} setLang={setLang} />
      </header>

      {/* HERO */}
      <section className={`${shell} pt-20 sm:pt-28 pb-24`}>
        <h1 className="font-serif font-light text-4xl sm:text-6xl leading-[1.04] tracking-[-0.01em] text-ink">
          {t.heroLines.map((line, i) => (
            <MaskLine key={line} delay={0.1 + i * 0.12}>
              {line}
            </MaskLine>
          ))}
        </h1>
        <p className="font-serif italic text-2xl text-ink/70 leading-snug mt-10 max-w-[520px]">
          {t.heroKicker}
        </p>
        <p className="text-lg leading-relaxed text-ink-soft mt-6 max-w-[520px]">
          {t.heroBody}
        </p>
        <div className="mt-12">
          <SubscribeForm
            t={t}
            lang={lang}
            source="hero"
            idPrefix="hero"
            buttonLabel={t.formButtonHero}
          />
        </div>
      </section>

      {/* MANIFESTO */}
      <section className={`${shell} py-24 border-t border-line`}>
        <Reveal>
          <Label>{lang === "es" ? "Manifiesto" : "Manifesto"}</Label>
        </Reveal>
        <Reveal>
          <h2 className="font-serif font-light text-3xl sm:text-5xl leading-[1.05] tracking-tight text-ink max-w-[600px]">
            {t.manifestoTitle}
          </h2>
        </Reveal>
        <div className="mt-10 space-y-6 max-w-[560px]">
          {t.manifestoParas.map((p, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <p className="text-lg leading-relaxed text-ink-soft">{p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className={`${shell} py-24 border-t border-line`}>
        <Reveal>
          <Label>{t.principlesLabel}</Label>
        </Reveal>
        <div className="mt-2">
          {t.principles.map((p, i) => (
            <Reveal key={p.name} delay={0.06 * i}>
              <div className="grid grid-cols-[auto_1fr] gap-6 sm:gap-10 py-9 border-t border-line first:border-t-0">
                <span className="font-serif text-2xl text-ink-faint leading-none pt-1 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl font-normal text-ink mb-3">
                    {p.name}
                  </h3>
                  <p className="text-lg leading-relaxed text-ink-soft max-w-[480px]">
                    {p.text}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className={`${shell} py-24 border-t border-line`}>
        <Reveal>
          <Label>{t.aboutLabel}</Label>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif font-light text-3xl sm:text-5xl leading-[1.05] tracking-tight mb-10 text-ink">
            {t.aboutTitle}
          </h2>
        </Reveal>
        <div className="space-y-6 max-w-[560px]">
          {t.aboutParas.map((p, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <p className="text-lg leading-relaxed text-ink-soft">{p}</p>
            </Reveal>
          ))}
          <Reveal delay={0.1}>
            <p className="font-serif italic text-2xl sm:text-3xl text-ink leading-snug pt-4">
              {t.aboutQuote}
            </p>
          </Reveal>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className={`${shell} py-24 border-t border-line`}>
        <Reveal>
          <Label>{t.newsletterLabel}</Label>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif font-light text-3xl sm:text-5xl leading-[1.05] tracking-tight mb-8 text-ink">
            {t.newsletterTitle}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-lg leading-relaxed text-ink-soft mb-2 max-w-[540px]">
            {t.newsletterIntro}
          </p>
          <p className="font-serif italic text-xl text-ink/75 leading-relaxed max-w-[540px] mb-12">
            {t.newsletterTopics}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <SubscribeForm
            t={t}
            lang={lang}
            source="newsletter"
            idPrefix="newsletter"
            buttonLabel={t.formButtonNewsletter}
          />
          <p className="mt-6 text-sm text-ink-faint leading-relaxed max-w-[440px]">
            {t.newsletterFine}
          </p>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-line">
        <div className={`${shell} py-16`}>
          <nav className="flex flex-wrap gap-x-8 gap-y-3">
            {t.footerLinks.map((label, i) => (
              <a
                key={label}
                href={label === "LinkedIn" ? "https://www.linkedin.com" : "#"}
                target={label === "LinkedIn" ? "_blank" : undefined}
                rel={label === "LinkedIn" ? "noopener noreferrer" : undefined}
                data-testid={`footer-link-${i}`}
                className="text-sm text-ink-soft hover:text-ink transition-colors duration-500"
              >
                {label}
              </a>
            ))}
          </nav>
          <p className="mt-8 font-serif italic text-lg text-ink-faint">
            {t.footerNote}
          </p>
          <p className="mt-2 text-xs tracking-wide text-ink-faint">
            © {new Date().getFullYear()} · pacomontero.com
          </p>
        </div>
      </footer>
    </div>
  );
}
